import { useMemo, useState } from 'react';
import { FolderClosed, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFolderTree } from '@utils/api/folders/use-folder-tree';
import { flattenFolderTree } from '@utils/folders/folder-tree';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import type { Folder } from '@utils/types/folders/folder';
import Input from '../ui-kit/input';
import Button from '../ui-kit/button';

type Props = {
  selectedFolder?: Folder | null;
  onSelect: (folder: Folder | undefined) => void;
  canMoveTo: (id: number | null) => boolean;
};

export function MoveFolderPicker({
  selectedFolder,
  onSelect,
  canMoveTo
}: Props) {
  const { t } = useTranslation();
  const { getColour, getBrandTint } = useThemeContext();
  const [search, setSearch] = useState('');
  const { data, isLoading, isError, refetch } = useFolderTree();
  const options = useMemo(() => flattenFolderTree(data), [data]);
  const matches = options.filter(option =>
    option.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  );
  const destinations = [
    { id: null, label: t('home'), depth: 0, folder: undefined },
    ...matches.map(option => ({ id: option.folder.id, ...option }))
  ];

  return (
    <div className="space-y-4">
      <Input
        aria-label={t('search')}
        placeholder={t('search')}
        value={search}
        onChange={event => setSearch(event.target.value)}
        type="text"
      />
      <fieldset className="rounded-2xl border border-hairline bg-app-soft p-3">
        <legend
          className="text-xs font-semibold px-1"
          style={{ color: getColour('inactiveText') }}
        >
          {t('available-folders')}
        </legend>
        {isLoading ? (
          <p role="status" className="text-sm p-3">
            {t('searching')}
          </p>
        ) : isError ? (
          <div role="alert" className="space-y-2 p-3">
            <p>{t('an-error-occurred')}</p>
            <Button
              label={t('retry')}
              size="small"
              variant="outline"
              onClick={() => void refetch()}
            />
          </div>
        ) : (
          <div className="space-y-1 max-h-[320px] overflow-y-auto">
            {destinations.map(destination => {
              const selected = (selectedFolder?.id ?? null) === destination.id;
              const disabled = !canMoveTo(destination.id);
              const Icon = destination.id === null ? Home : FolderClosed;
              return (
                <label
                  key={destination.id ?? 'home'}
                  className="flex items-center gap-3 p-3 rounded-xl border transition-colors"
                  style={{
                    color: getColour('normalText'),
                    borderColor:
                      selected && !disabled
                        ? getColour('activeText')
                        : 'transparent',
                    backgroundColor:
                      selected && !disabled
                        ? getBrandTint(0.12)
                        : 'transparent',
                    paddingLeft: 12 + destination.depth * 12,
                    opacity: disabled ? 0.5 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer'
                  }}
                >
                  <input
                    type="radio"
                    name="selectedFolder"
                    checked={selected && !disabled}
                    disabled={disabled}
                    onChange={() => onSelect(destination.folder)}
                    style={{ accentColor: getColour('activeText') }}
                    className="h-4 w-4 shrink-0"
                  />
                  <Icon
                    size={18}
                    className="shrink-0"
                    style={{ color: getColour('icon') }}
                  />
                  <span
                    className="text-sm font-medium min-w-0 break-words"
                    title={destination.label}
                  >
                    {destination.label}
                  </span>
                </label>
              );
            })}
            {matches.length === 0 && (
              <p
                className="text-sm text-center py-4"
                style={{ color: getColour('inactiveText') }}
              >
                {t('no-folders-found')}
              </p>
            )}
          </div>
        )}
      </fieldset>
    </div>
  );
}
